import { SignInPage } from '@backstage/core-components';
import {
  configApiRef,
  IdentityApi,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { useCallback } from 'react';

// Dynamic sign-in page that branches on auth.environment (frontend-visible config).
// NOTE: app.auth.providers is NOT @visibility frontend, so we cannot read it in production.
// All provider definitions are hardcoded per environment here.
const DynamicSignInPage = (props: any) => {
  const configApi = useApi(configApiRef);
  const environment = configApi.getOptionalString('auth.environment');

  // PR preview: the OAuth popup callback routes through Dev Backstage, which
  // signs the token with Dev's keys. The PR preview backend can't always verify
  // this immediately. Force ONE page reload after the initial sign-in so the app
  // re-initializes with the session cookie — the reload's identity check goes
  // through the PR preview backend directly, issuing a correctly-signed token.
  // sessionStorage flag prevents infinite reload loop (auto sign-in fires again
  // after reload).
  const handlePrPreviewSignIn = useCallback(
    (identity: IdentityApi) => {
      if (!sessionStorage.getItem('pr-preview-reloaded')) {
        sessionStorage.setItem('pr-preview-reloaded', '1');
        window.location.reload();
        return;
      }
      sessionStorage.removeItem('pr-preview-reloaded');
      props.onSignInSuccess(identity);
    },
    [props],
  );

  // PR previews: auto sign-in with Microsoft (routes callback through Dev Backstage)
  if (environment === 'pr-preview') {
    return (
      <SignInPage
        {...props}
        onSignInSuccess={handlePrPreviewSignIn}
        auto
        provider={{
          id: 'microsoft-auth-provider',
          title: 'Microsoft',
          message: 'Sign in using Microsoft Entra ID',
          apiRef: microsoftAuthApiRef,
        }}
      />
    );
  }

  // Localhost only: Guest + Microsoft (Dev/Prod ECS use auth.environment=production)
  if (environment === 'development') {
    return (
      <SignInPage
        {...props}
        providers={[
          'guest',
          {
            id: 'microsoft-auth-provider',
            title: 'Microsoft',
            message: 'Sign in using Microsoft Entra ID',
            apiRef: microsoftAuthApiRef,
          },
        ]}
      />
    );
  }

  // Production (and any other environment): auto sign-in with Microsoft only
  return (
    <SignInPage
      {...props}
      auto
      provider={{
        id: 'microsoft-auth-provider',
        title: 'Microsoft',
        message: 'Sign in using Microsoft Entra ID',
        apiRef: microsoftAuthApiRef,
      }}
    />
  );
};

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props => <DynamicSignInPage {...props} />,
  },
});

export const signInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [signInPage],
});
